import React, { useState, useEffect, useRef } from 'react';
import {
    ReceiptLong,
    VisibilityOutlined,
    CalendarTodayOutlined,
    Inventory2Outlined
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '../../utils/apiService';
import { useAuth } from '../../AuthContext';
import apiLinks from '../../config/apiLinks.json';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InvoiceDetailSheet from './InvoiceDetailSheet'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InvoiceStepper from './InvoiceStepper'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
const formatPrice = (price) =>
    Number(price).toLocaleString('fa-IR');
const InvoiceSkeletonItem = () => {
    return (
        <div className="invoice-card skeleton-card">
            {/* Header */}
            <div className="skeleton-row space-between">
                <div className="skeleton-line w-40 h-14" />
                <div className="skeleton-circle" />
            </div>

            {/* Meta */}
            <div className="skeleton-row mt-2">
                <div className="skeleton-line w-30" />
            </div>

            {/* Footer */}
            <div className="skeleton-row space-between mt-3">
                <div className="skeleton-line w-25" />
                <div className="skeleton-pill w-20" />
            </div>
        </div>
    );
};
const Invoices = () => {
    const { logout, autoLogin, accessToken } = useAuth();
    const [page, setPage] = useState(1);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [invoiceLevels, setInvoiceLevels] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const containerRef = useRef(null);
    const [stepperOpenId, setStepperOpenId] = useState(null);

    const fetchInvoiceLevels = async () => {
        try {
            const response = await apiService.get(
                `${apiLinks.baseUrl}${apiLinks.Invoices.Get_Invoice_Levels}`,
                {},
                autoLogin
            );            
            setInvoiceLevels(response.Invoice_Levels || []);
        } catch (error) {
            console.error('Error fetching invoices levels data:', error);
        } finally {
        }
    };
const fetchData = async (pageNumber = 1) => {
    try {
        setLoading(true);
        const response = await apiService.get(
            `${apiLinks.baseUrl}${apiLinks.Invoices.Get_Invoices}?_page=${pageNumber}&_limit=10`,
            {},
            autoLogin
        );
        
        if (response && response.length > 0) {
            setData(prev => {
                const newItems = response.filter(newItem => 
                    !prev.some(existingItem => existingItem.ShomareFactor === newItem.ShomareFactor)
                );
                return [...prev, ...newItems];
            });
        } else {
            setHasMore(false);
        }
    } catch (error) {
        console.error('Error fetching invoices data:', error);
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {
        if (!accessToken) return;
        if (page === 1 && data.length > 0) return; // already fetched first page
        fetchData(page);
    }, [page, accessToken]);
    useEffect(() => {
        if (!accessToken) return;
        // Reset state
        setData([]);
        setPage(1);
        setHasMore(true);
        fetchData(1); // fetch first page immediately
    }, [accessToken]);

    useEffect(() => {
        fetchInvoiceLevels()
    }, [accessToken]);
    const downloadInvoicePdf = async (invoice) => {
        try {
            setDownloadingId(invoice.ShomareFactor);

            const response = await apiService.post(
                `${apiLinks.baseUrl}${apiLinks.InvoiceReports.InvoiceReports}`,
                [
                    { ShomareFactor: Number(invoice.ShomareFactor) },
                    { CodeMoshtari: Number(invoice.CodeMoshtari) }
                ],
                autoLogin
            );

            const data = response.data;

            if (!data || !data.base64Content) {
                console.error("No base64Content received from API");
                return;
            }

            const cleanedBase64 = data.base64Content.replace(/\s/g, '');

            const byteCharacters = atob(cleanedBase64);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: data.mimeType || 'application/pdf' });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Invoice-${invoice.ShomareFactor}.pdf`;
            link.click();

            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading invoice PDF:', error);
        } finally {
            setDownloadingId(null);
        }
    };
    const handleScroll = () => {
        if (!containerRef.current || loading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            setPage(prev => prev + 1);
        }
    };

    return (
        <>
            <div className="page-content">
                <div
                    className="small-container"
                    ref={containerRef}
                    onScroll={handleScroll}
                    style={{ overflowY: 'auto', maxHeight: '80vh' }}
                >
                    {loading && page === 1 ? (
                        <div className="invoices-list">
                            {[...Array(6)].map((_, i) => (
                                <InvoiceSkeletonItem key={i} />
                            ))}
                        </div>
                    ) : data.length === 0 && !loading ? (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="empty-invoice-box"
                        >
                            <ReceiptLongIcon className="empty-icon" />
                            <h5>هنوز فاکتوری ثبت نشده است</h5>
                            <p>
                                پس از ثبت اولین فاکتور، اطلاعات آن در این بخش نمایش داده می‌شود.
                            </p>
                        </motion.div>
                    ) : (
                        <div className="invoices-list">
                            {data.map((item, index) => (
                                <motion.div
                                    key={item.number || index}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.02 }}
                                    className="invoice-card"
                                >
                                    {/* Header */}
                                    <div
                                        className="invoice-header"
                                        onClick={() => {
                                            setSelectedInvoice(item);
                                            setDetailOpen(true);
                                        }}
                                    >
                                        <div className="invoice-title">
                                            <ReceiptLong className="icon" />
                                            <span>فاکتور #{item.ShomareFactor}</span>
                                        </div>
                                        <span className="branch">{item.CodeMoshtari}</span>
                                    </div>

                                    {/* Info */}
                                    <div
                                        className="invoice-info"
                                        onClick={() => {
                                            setSelectedInvoice(item);
                                            setDetailOpen(true);
                                        }}
                                    >
                                        <div>
                                            <CalendarTodayOutlined fontSize="small" />
                                            <span>{item.TarikhFactor}</span>
                                        </div>
                                        <div>
                                            <Inventory2Outlined fontSize="small" />
                                            <span>{item.InvoiceDetails[0]?.TedadeKartan} کارتن</span>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="invoice-footer">
                                        <div className="price">
                                            {formatPrice(item.MablaghGhabelePardakht)} ریال
                                        </div>
                                        <div className="actions">
                                            <button
                                                onClick={() => {
                                                    setSelectedInvoice(item);
                                                    setDetailOpen(true);
                                                }}
                                            >
                                                <VisibilityOutlined />
                                            </button>
                                            <button
                                                onClick={() => downloadInvoicePdf(item)}
                                                title="دانلود PDF فاکتور"
                                                disabled={downloadingId === item.ShomareFactor}
                                            >
                                                {downloadingId === item.ShomareFactor ? '...' : <FileDownloadOutlinedIcon />}
                                            </button>
                                            {/* New Icon to toggle stepper */}
                                            <button
                                                onClick={() =>
                                                    setStepperOpenId(stepperOpenId === item.ShomareFactor ? null : item.ShomareFactor)
                                                }
                                                title="نمایش مراحل"
                                            >
                                                <InfoOutlinedIcon />
                                            </button>
                                        </div>
                                    </div>
                                    <AnimatePresence>
                                        {stepperOpenId === item.ShomareFactor && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0, y: -10 }}
                                                animate={{ opacity: 1, height: "auto", y: 0 }}
                                                exit={{ opacity: 0, height: 0, y: -10 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                style={{ overflow: "hidden" }}
                                            >
                                                <InvoiceStepper
                                                    levels={invoiceLevels}
                                                    currentStatus={Number(item.Invoice_Level)}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                </motion.div>
                            ))}

                            {/* Loader for next page */}
                            {loading && page > 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '10px 0',
                                }}>
                                    <div style={{
                                        border: '3px solid #f3f3f3',
                                        borderTop: '3px solid #ff4d4f',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        animation: 'spin 1s linear infinite'
                                    }} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <InvoiceDetailSheet
                open={detailOpen}
                data={selectedInvoice}
                onClose={() => setDetailOpen(false)}
            />
        </>
    );
};

export default Invoices;
